import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router";
import { collectiveDetails as bio } from "../assets/componentDetails";
import { collectiveTabs } from "../assets/pageTabs";
import CompanyGrid from "../components/CompanyGrid";
import GridController from "../components/GridController";
import Header from "../components/Header";
import Loading from "../components/Loading";
import usePageReset from "../hooks/usePageReset";
import useRelation from "../hooks/useRelation";
import { CollectiveCompanies } from "../schema";
import structuredData from "../assets/structuredText";

export default function Collective() {
  const { companyType } = useParams();
  const [displayed, setDisplayed] = useState<string>((companyType as string) || "members");
  const navigate = useNavigate();
  const chooseTab = useCallback(
    (id: string) => {
      setDisplayed(id);
    },
    [setDisplayed]
  );
  usePageReset();

  useEffect(() => {
    const typeFound = Object.keys(CollectiveCompanies).includes(companyType as string);
    if (typeFound) setDisplayed(companyType as string);
    if (!typeFound && companyType) navigate(`/404`);
  }, [companyType]);
  const [selectedTab, isLoading] = useRelation(displayed == "members" ? "collective" : "partner");
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="keywords" content="members collective companies cultiv" />
        <meta
          name="description"
          content="Meet our collective members, the diverse group of independent companies
that share Cultiv's vision, collaborating to drive innovation and entrepreneurship."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:url" content="https://cultiv.llc" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          itemProp="image"
          content="https://cultiv.llc/images/metaimg.jpg"
        />
        <link rel="image_src" itemProp="image" href="https://cultiv.llc/images/metaimg.jpg" />
        <meta
          property="og:description"
          content="Meet our collective members, the diverse group of independent companies
that share Cultiv's vision, collaborating to drive innovation and entrepreneurship."
        />
        <meta property="og:site_name" content="Cultiv LLC" />
        <meta property="og:title" content="Cultiv | Collective" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://cultiv.llc" />
        <meta property="twitter:title" content="Cultiv | Collective" />
        <meta
          property="twitter:description"
          content="Meet our collective members, the diverse group of independent companies
that share Cultiv's vision, collaborating to drive innovation and entrepreneurship."
        />
        <meta property="twitter:image" content="https://cultiv.llc/images/metaimg.jpg" />
        <link rel="canonical" href="https://cultiv.llc" />
        <script type="application/ld+json">{structuredData}</script>

        <title>Collective</title>
      </Helmet>
      <div className="w-full flex flex-col justify-center items-center lg:gap-10 gap-6">
        <Header desc={bio.text} color="bg-cultiv-yellow" />
        <div
          id="members"
          className="flex flex-col lg:gap-10 gap-6 w-full items-center justify-center md:px-15 lg:px-20 xl:px-30 px-6"
        >
          <GridController
            tabs={collectiveTabs}
            displayed={displayed}
            setDisplayed={chooseTab}
            page="collective"
          />
          {isLoading ? (
            <div className="min-h-[50vh] sm:min-h-[70vh] flex items-center justify-center">
              <Loading />
            </div>
          ) : (
            <CompanyGrid cards={selectedTab} />
          )}
        </div>
      </div>
    </>
  );
}
